import { SnowplowModuleContext } from '@/helpers/tracking/providers';
import { GetTitleContextArguments, TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ObjectType } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getPromoText, PromoTextWithIcon } from '@/helpers/provider-promotion';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

export type LastVistedTitleDetails = {
	titleId: string;
	titleName: string;
	isTitleUpcoming?: boolean;
	offers?: TitleDetail['offers'];
	fullPath?: string;
	titleObjectId?: number;
	titleObjectType?: ObjectType;
};
export const useTitleDetailsStore = defineStore('titleDetails', () => {
	const permanentAudiences = ref<string[]>([]);
	const moduleContext = ref<SnowplowModuleContext | null>(null);

	const titleContextArguments = ref<GetTitleContextArguments | null>(null);
	const sponsoredAd = ref<SponsoredAdFragment | undefined>(undefined);
	const lastVisitedTitleIdState = ref('');
	const lastVisitedTitleNameState = ref('');
	const lastVisitedTitleDetails = ref<LastVistedTitleDetails>({
		titleId: '',
		titleName: '',
		isTitleUpcoming: false,
		offers: [],
		fullPath: '',
		titleObjectId: undefined,
		titleObjectType: undefined,
	});
	const didFetchPromotion = ref(false);
	const providerPromotion = ref<PromoTextWithIcon | null>(null);
	const inLists = ref<string[]>([]);

	const refferalModuleContext = computed(() => {
		return moduleContext.value;
	});

	const titleContext = computed(() => {
		return titleContextArguments.value;
	});

	const getPermanentAudiences = computed(() => {
		return permanentAudiences.value;
	});

	const lastVisitedTitleId = computed(() => {
		return lastVisitedTitleDetails.value.titleId;
	});

	const lastVisitedTitleName = computed(() => {
		return lastVisitedTitleDetails.value.titleName;
	});

	function setSponsoredRecommendation(ad: SponsoredAdFragment | undefined) {
		sponsoredAd.value = ad;
	}

	function setTitleContext(payload: GetTitleContextArguments) {
		titleContextArguments.value = payload;
	}

	function clearTitleContext() {
		titleContextArguments.value = null;
	}

	function setPermanentAudiences(audiences: string[] = []) {
		permanentAudiences.value = audiences;
		TrackingHelper.refreshPermanentAudiences(audiences);
	}

	function resetPermanentAudiences() {
		permanentAudiences.value = [];
	}

	function setLastVisitedTitleDetails(details: LastVistedTitleDetails) {
		lastVisitedTitleDetails.value = details;
	}

	function setInLists(lists: string[]) {
		inLists.value = lists;
	}

	function clearInLists() {
		inLists.value = [];
	}

	function setProviderPromotion(title?: TitleDetail) {
		if (title == null) return;
		const languageStore = useLanguageStore();
		const country = languageStore.country;
		const promotion = getPromoText(country, title);
		providerPromotion.value = promotion;
		didFetchPromotion.value = true;
	}

	return {
		permanentAudiences,
		moduleContext,
		titleContextArguments,
		sponsoredAd,
		lastVisitedTitleIdState,
		lastVisitedTitleNameState,
		lastVisitedTitleDetails,
		didFetchPromotion,
		providerPromotion,

		refferalModuleContext,
		titleContext,
		getPermanentAudiences,
		lastVisitedTitleId,
		lastVisitedTitleName,

		setSponsoredRecommendation,
		setTitleContext,
		clearTitleContext,
		setPermanentAudiences,
		resetPermanentAudiences,
		setLastVisitedTitleDetails,
		setProviderPromotion,
		inLists,
		setInLists,
		clearInLists,
	};
});
